/* eslint-disable @typescript-eslint/no-unused-vars */
/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import { IconDotsVertical, IconPlus, IconMinus } from "@tabler/icons-react";
import NormalSearch from "../../../components/common/NormalSearch";
import ListView from "../../../components/common/ListView";
import ToggleButton from "../../../components/ToggleButton";
import { useForm } from "react-hook-form";
import {
  useFetchTireData,
  useTireCreateMutation,
  useUpdateTireStatus,
  useUpdateTireData, // <-- Ensure this exists in your API hooks for updating
} from "../../../apis/zoneAndTierApis";
import toast from "react-hot-toast";
import formatDate from "../../../utilities/helper";
import MessageModal from "../../../components/MessageModal";
import AddTireModal from "./AddTireModal";
import ViewCitiesModal from "./ViewCitiesModal";
import RoleToggle from "../../../components/RoleToggle";
import NoDataFound from "../../../components/common/NoDataFound";
import TableSkeleton from "../../../components/skeleton/TableSkeleton";
import Pagination from "../../../components/common/Pagination";
import useDebounce from "../../../utilities/useDebounce";

type CoverageFormValues = {
  name: string;
  countryId: string | null;
  warehouseId: string;
  coverageCity: string | null;
};
type Props = {
  options: string[];
  active: string;
  onChange: React.Dispatch<React.SetStateAction<string>>;
};

export default function TireListTab({ options, active, onChange }: Props) {
  const [searchFor, setSearchFor] = useState("");
  const [pageNo, setPageNo] = useState(1);
  const [limit, setLimit] = useState(10);
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [tireItem, setTireItem] = useState<any>(null);
  const [editable, setEditable] = useState(false);
  const [coverageCityError, setCoverageCityError] = useState<string>("");

  const { mutate, isPending: createPending } = useTireCreateMutation();
  const { mutate: updateTire, isPending: updatePending } = useUpdateTireData();
  const {
    data: tireData,
    refetch: tireRefetch,
    isLoading,
    isFetching,
  } = useFetchTireData(pageNo, limit, searchFor);
  const { mutate: updateTireStatus, isPending: statusPending } =
    useUpdateTireStatus();

  const [tireDataArray, setTireDataArray] = useState<any>(tireData);
  const [actionModal, setActionModal] = useState(false);
  const [status, setStatus] = useState<string>("");
  const [citiesArray, setCitiesArray] = useState<any>([]);
  const [isViewCitiesOpen, setIsViewCitiesOpen] = useState(false);

  const [openDropdownIndex, setOpenDropdownIndex] = useState<number | null>(
    null
  );
  const dropdownRef = useRef<HTMLDivElement>(null);
  const [selectedWarehouseId] = useState<string>("wh123");

  const {
    register,
    handleSubmit,
    clearErrors,
    reset,
    watch,
    formState: { errors },
    setValue,
    control,
  } = useForm<CoverageFormValues>();

  useEffect(() => {
    if (tireData) setTireDataArray(tireData);
  }, [tireData]);

  useEffect(() => {
    function handleClickOutside(event: MouseEvent) {
      if (
        dropdownRef.current &&
        !dropdownRef.current.contains(event.target as Node)
      ) {
        setOpenDropdownIndex(null);
      }
    }
    document.addEventListener("mousedown", handleClickOutside);
    return () => document.removeEventListener("mousedown", handleClickOutside);
  }, []);

  const resetModalState = () => {
    setTireItem(null);
    setEditable(false);
    setCoverageCityError("");
    reset({
      name: "",
      countryId: null,
      warehouseId: "", 
    }); // Explicitly reset to default values
    setIsModalOpen(false); // Critical: close the modal
  };

  const handleToggleModal = () => {
    if (isModalOpen) {
      resetModalState(); // Now properly closes + resets everything
    } else {
      setIsModalOpen(true);
      setEditable(false);
      setTireItem(null);
      reset(); // Optional: clear form when opening fresh
    }
  };

  const handleAddTire = (data: any) => {
    if (!data?.coverageCity || data?.coverageCity?.length === 0) {
      setCoverageCityError("Please select at least one city");
      return;
    }
    setCoverageCityError("");
    mutate(data, {
      onSuccess: () => {
        toast.success("Tier created successfully");
        resetModalState();
        tireRefetch();
      },
      onError: (error) => toast.error(`${error}`),
    });
  };

  const handleUpdateTire = (data: any) => {
    if (!tireItem) return;
    if (!data?.coverageCity || data?.coverageCity?.length === 0) {
      setCoverageCityError("Please select at least one city");
      return;
    }
    setCoverageCityError("");
    updateTire(
      { id: tireItem.id, ...data },
      {
        onSuccess: () => {
          toast.success("Tier updated successfully");
          resetModalState();
          tireRefetch();
        },
        onError: (error) => toast.error(`${error}`),
      }
    );
  };

  const handleEditData = (item: any) => {
    setEditable(true);
    setTireItem(item);
    reset({
      name: item.name,
      warehouseId: item.warehouseId,
      countryId: item.countryId,
      coverageCity: item.coverageCityIds || [], // Adjust based on API field
    });
    setIsModalOpen(true);
  };

  const handleToggleStatus = (tire: any) => {
    setTireItem(tire);
    setStatus(tire.status);
    setActionModal(true);
  };

  const handleUpdateStatus = async () => {
    const newStatus = tireItem?.status === "ACTIVE" ? "INACTIVE" : "ACTIVE";
    const updated = tireDataArray.tires.map((v: any) =>
      v.id === tireItem?.id ? { ...v, status: newStatus } : v
    );
    setTireDataArray({ ...tireDataArray, tires: updated });

    await updateTireStatus({ id: tireItem?.id, status: newStatus });
    setActionModal(false);
    setTireItem(null);
  };

  const handleViewCities = (cities: any) => {
    setCitiesArray(cities);
    setIsViewCitiesOpen(true);
  };

  const debouncedSearchFor = useDebounce(searchFor, 550);

  useEffect(() => {
    tireRefetch();
  }, [debouncedSearchFor, pageNo, limit]);

  const handlePaginationFetch = (page: number) => {
    setPageNo(page);
  };

  return (
    <div>
      <div className="space-y-4">
        {/* LIST TABLE */}
        <div className="rounded-[20px]">
          <div className="flex items-start">
            <div className="bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5] flex items-center justify-between px-6 py-3 w-full rounded-tl-[20px] rounded-tr-[30px]">
              <RoleToggle
                options={options}
                active={active}
                onChange={onChange}
              />
              {/* <h1 className="text-lg font-medium text-[#212121]">Tire Lists</h1> */}

              <div className="flex gap-2">
                <NormalSearch
                  value={searchFor}
                  onChange={(e) => {
                    setSearchFor(e.target.value);
                    setPageNo(1);
                  }}
                />
                <button
                  type="button"
                  onClick={handleToggleModal}
                  className="bg-[#FBD037] text-[#212121] text-sm font-semibold p-2 rounded-full"
                >
                  {isModalOpen ? (
                    <IconMinus stroke={1.5} size={24} />
                  ) : (
                    <IconPlus stroke={1.5} size={24} />
                  )}
                </button>
              </div>
            </div>
          </div>
          {/* ADD/EDIT INLINE CARD */}

          {isModalOpen && (
            <div className=" p-4 border-t bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5] space-y-4">
              <div className="flex items-center justify-between">
                <h1 className="text-lg font-medium text-[#212121]">
                  {editable ? "Edit Tier" : "Add Tier"}
                </h1>
              </div>

              <AddTireModal
                selectedWarehouse={selectedWarehouseId}
                onClose={resetModalState}
                register={register}
                errors={errors}
                setValue={setValue}
                watch={watch}
                clearErrors={clearErrors}
                control={control}
                handleSubmit={() =>
                  editable
                    ? handleSubmit(handleUpdateTire)()
                    : handleSubmit(handleAddTire)()
                }
                coverageCityError={coverageCityError}
                setCoverageCityError={setCoverageCityError}
                isPending={editable ? updatePending : createPending}
                editable={editable}
                tireItem={tireItem}
                setTireItem={setTireItem}
              />
            </div>
          )}

          {isLoading ? (
            <TableSkeleton
              mainClass="bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5]"
              subClassName=""
              columns={[
                "#",
                "Tier Name",
                "Warehouse",
                "Country",
                "Date",
                "Status",
                "Action",
              ]}
              rows={6}
            />
          ) : tireDataArray?.tires?.length ? (
            <ListView
              tdClassName="rounded-r-none rounded-l-none"
              trClassName="hover:bg-[#FBD037]/20"
              mainDivClassName="rounded-b-[20px] bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5]"
              mailDivClassName="p-0 bg-transparent rounded-r-none rounded-br-3xl rounded-b-[20px]"
              thClassName="border-t"
              data={tireDataArray?.tires ?? []}
              columns={[
                {
                  label: "#",
                  render: (_: any, i: number) => (pageNo - 1) * limit + (i + 1),
                },
                { label: "Tier Name", render: (c: any) => c.name },
                { label: "Warehouse", render: (c: any) => c.warehouseName },
                { label: "Country", render: (c: any) => c.countryName },
                { label: "Date", render: (c: any) => formatDate(c.createdAt) },
                {
                  label: "Status",
                  render: (c: any) => (
                    <div className="flex justify-center">
                      <ToggleButton
                        onChange={() => handleToggleStatus(c)}
                        checked={c.status === "ACTIVE"}
                      />
                    </div>
                  ),
                },
                {
                  label: "Actions",
                  render: (c: any) => (
                    <div className="relative flex justify-center">
                      <button
                        onClick={() =>
                          setOpenDropdownIndex(
                            openDropdownIndex === c ? null : c
                          )
                        }
                        className="p-2 border rounded-full bg-[#F7F6F2]"
                      >
                        <IconDotsVertical size={20} />
                      </button>
                      {openDropdownIndex === c && (
                        <div
                          ref={dropdownRef}
                          className="absolute bg-[#F7F6F2] divide-y divide-[#979797]/50 right-20 w-28 -top-8 z-50 mt-2 rounded-xl shadow-lg overflow-hidden"
                        >
                          <div
                            onClick={() =>
                              handleViewCities(c.coverageCityNames)
                            }
                            className="px-4 py-2 text-[#404040] hover:bg-[#FBD037] cursor-pointer"
                          >
                            View Cities
                          </div>
                          <div
                            onClick={() => handleEditData(c)}
                            className="px-4 py-2 text-[#404040] hover:bg-[#FBD037] cursor-pointer"
                          >
                            Edit
                          </div>
                        </div>
                      )}
                    </div>
                  ),
                },
              ]}
            />
          ) : (
            <NoDataFound
              subClassName=""
              mainClassName="bg-gradient-to-r from-[#FFFFFD] to-[#FDF8E5] rounded-b-3xl"
            />
          )}

          {tireDataArray?.totalPages > 1 && (
            <div className="flex justify-center items-center py-4">
              <Pagination
                currentPage={pageNo}
                totalPages={tireDataArray?.totalPages}
                fetchData={handlePaginationFetch}
              />
            </div>
          )}
        </div>
      </div>

      {/* Confirm Modal */}
      <MessageModal
        open={actionModal}
        onClose={() => setActionModal(false)}
        title="Confirmation Required"
        subtitle="Are you sure you want to perform this action?"
        img={status === "ACTIVE" ? "/img/inactive.svg" : "/img/active.svg"}
        showFooter={true}
        closeButtonText="Cancel"
        actionButtonText="Confirm"
        actionButtonOnClick={handleUpdateStatus}
        isPending={statusPending}
        size="md"
      />

      <ViewCitiesModal
        citiesItem={citiesArray ?? []}
        open={isViewCitiesOpen}
        onClose={() => setIsViewCitiesOpen(false)}
      />
    </div>
  );
}
